import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EZIOKResult } from '../libs/EZIOKService';
import axiosClient from '../libs/axiosClient';
import {
  logAuthSuccess,
  logAuthFailure,
  addAuthBreadcrumb,
  logAuthAPIError,
} from '../libs/sentryAuth';

/**
 * EZ-IOK 간편인증 콜백 페이지
 *
 * 라우터에 등록 필요:
 * <Route path="/auth/eziok/callback" element={<EZIOKCallback />} />
 *
 * 두 가지 방식 지원:
 * 1. callback 방식 (팝업): window.opener.postMessage로 결과 전달
 * 2. redirect 방식 (인앱 브라우저): 저장된 복귀 경로로 redirect
 */
function EZIOKCallback() {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState<EZIOKResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('간편인증 결과를 처리하고 있습니다...');

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Sentry: 콜백 페이지 진입 로깅
        addAuthBreadcrumb('EZ-IOK callback page entered', 'callback', {
          search: window.location.search,
          hasOpener: !!window.opener,
        }, 'info');

        console.log('[EZIOKCallback] Processing callback...');
        console.log('[EZIOKCallback] URL params:', Object.fromEntries(searchParams.entries()));
        console.log('[EZIOKCallback] Has opener:', !!window.opener);

        // 1. URL에서 clientTxId 먼저 확인 (redirect 방식)
        let txId = searchParams.get('clientTxId');

        // 2. sessionStorage 폴백 (callback 방식)
        if (!txId) {
          txId = sessionStorage.getItem('eziok_txId');
          console.log('[EZIOKCallback] txId from sessionStorage:', txId);
        } else {
          console.log('[EZIOKCallback] txId from URL:', txId);
        }

        // 3. keyToken 확인 (백엔드에서 결과 조회 필요)
        const keyToken = searchParams.get('keyToken');
        const resultCode = searchParams.get('resultCode');
        const errorMsg = searchParams.get('errorMsg') || searchParams.get('message');

        // 에러 체크 (EZ-IOK 성공 코드: 0)
        if (resultCode && resultCode !== '0') {
          const errMessage = errorMsg || '간편인증에 실패했습니다.';
          logAuthFailure('easy', errMessage, {
            resultCode,
            errorMsg: errorMsg || undefined,
          });
          handleError(new Error(errMessage));
          return;
        }

        // keyToken이 있으면 백엔드에서 결과 조회
        if (keyToken && txId) {
          setStatusMessage('인증 결과를 조회하고 있습니다...');

          try {
            const response = await axiosClient.post('/func/eziok/result', {
              keyToken,
              txId,
            });

            console.log('[EZIOKCallback] Result API response:', response.data);

            // sessionStorage 정리
            sessionStorage.removeItem('eziok_txId');

            // EZ-IOK 성공 코드: 0000
            if (response.data.resultCode === '0000') {
              const eziokResult: EZIOKResult = {
                success: true,
                message: response.data.resultMsg || '인증 성공',
                data: {
                  name: response.data.name,
                  ci: response.data.ci,
                  di: response.data.di,
                  birthDate: response.data.birthDate,
                  gender: response.data.gender,
                  nationalInfo: response.data.nationalInfo,
                  mobileNo: response.data.mobileNo,
                },
              };
              logAuthSuccess('easy', { txId, resultCode: response.data.resultCode });
              handleSuccess(eziokResult);
            } else {
              logAuthFailure('easy', response.data.resultMsg || '인증 결과 조회 실패', {
                txId,
                resultCode: response.data.resultCode,
                resultMsg: response.data.resultMsg,
              });
              handleError(new Error(response.data.resultMsg || '인증 결과 조회에 실패했습니다.'));
            }
          } catch (apiError) {
            console.error('[EZIOKCallback] Result API error:', apiError);
            logAuthAPIError('easy', '/func/eziok/result', apiError, { txId });
            handleError(apiError instanceof Error ? apiError : new Error('인증 결과 조회 중 오류가 발생했습니다.'));
          }
          return;
        }

        // 백엔드에서 직접 쿼리 파라미터로 전달한 경우 (기존 방식)
        const success = searchParams.get('success') === 'true';
        const message = searchParams.get('message') || '';
        const name = searchParams.get('name') || undefined;
        const ci = searchParams.get('ci') || undefined;
        const di = searchParams.get('di') || undefined;
        const birthDate = searchParams.get('birthDate') || undefined;
        const gender = searchParams.get('gender') || undefined;
        const nationalInfo = searchParams.get('nationalInfo') || undefined;
        const mobileNo = searchParams.get('mobileNo') || undefined;

        if (success) {
          const eziokResult: EZIOKResult = {
            success,
            message,
            data: { name, ci, di, birthDate, gender, nationalInfo, mobileNo },
          };
          logAuthSuccess('easy', { rawResponse: window.location.search });
          handleSuccess(eziokResult);
        } else if (message) {
          logAuthFailure('easy', message, { rawResponse: window.location.search });
          handleError(new Error(message));
        } else {
          // txId도 keyToken도 없는 경우
          handleError(new Error('인증 정보가 유효하지 않습니다. 다시 시도해주세요.'));
        }
      } catch (err) {
        console.error('[EZIOKCallback] Unexpected error:', err);
        handleError(err instanceof Error ? err : new Error('인증 처리 중 오류가 발생했습니다.'));
      }
    };

    const handleSuccess = (eziokResult: EZIOKResult) => {
      console.log('[EZIOKCallback] Auth success:', eziokResult);
      setResult(eziokResult);
      setIsLoading(false);

      // 부모 창이 있으면 postMessage (callback 방식)
      if (window.opener) {
        console.log('[EZIOKCallback] Sending postMessage to opener');
        window.opener.postMessage(eziokResult, '*');
        setTimeout(() => window.close(), 1000);
      } else {
        // 부모 창이 없으면 저장된 복귀 경로로 redirect (redirect 방식)
        const returnPath = sessionStorage.getItem('auth_return_path');
        console.log('[EZIOKCallback] No opener, return path:', returnPath);

        // 세션 정리
        sessionStorage.removeItem('auth_return_path');
        sessionStorage.removeItem('eziok_txId');

        if (returnPath) {
          // 결과를 URL 파라미터로 전달하여 복귀
          const returnUrl = new URL(returnPath);
          returnUrl.searchParams.set('authSuccess', 'true');
          returnUrl.searchParams.set('authMethod', 'eziok');
          // 민감정보는 sessionStorage로 전달
          sessionStorage.setItem('eziok_result', JSON.stringify(eziokResult));

          setStatusMessage('원래 페이지로 이동합니다...');
          setTimeout(() => {
            window.location.href = returnUrl.toString();
          }, 500);
        } else {
          // 복귀 경로도 없으면 sessionStorage에 저장하고 히스토리 백
          sessionStorage.setItem('eziok_result', JSON.stringify(eziokResult));
          setStatusMessage('원래 페이지로 이동합니다...');
          setTimeout(() => {
            window.history.back();
          }, 500);
        }
      }
    };

    const handleError = (err: Error) => {
      console.error('[EZIOKCallback] Auth error:', err);
      setError(err);
      setIsLoading(false);

      // 부모 창이 있으면 postMessage
      if (window.opener) {
        window.opener.postMessage({
          success: false,
          message: err.message,
          data: {},
        }, '*');
        setTimeout(() => window.close(), 2000);
      } else {
        // 부모 창이 없으면 저장된 복귀 경로로 redirect
        const returnPath = sessionStorage.getItem('auth_return_path');

        // 세션 정리
        sessionStorage.removeItem('auth_return_path');
        sessionStorage.removeItem('eziok_txId');

        if (returnPath) {
          const returnUrl = new URL(returnPath);
          returnUrl.searchParams.set('authSuccess', 'false');
          returnUrl.searchParams.set('authError', err.message);
          sessionStorage.setItem('eziok_error', JSON.stringify({ message: err.message }));

          setTimeout(() => {
            window.location.href = returnUrl.toString();
          }, 2000);
        } else {
          sessionStorage.setItem('eziok_error', JSON.stringify({ message: err.message }));
        }
      }
    };

    processCallback();
  }, [searchParams]);

  // 부모 창 존재 여부 로깅
  useEffect(() => {
    console.log('[EZIOKCallback] Component mounted');
    console.log('[EZIOKCallback] window.opener:', !!window.opener);
    console.log('[EZIOKCallback] auth_return_path:', sessionStorage.getItem('auth_return_path'));
  }, []);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #333',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{ color: '#666', fontSize: '14px' }}>
          {statusMessage}
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <p style={{ color: '#e74c3c', fontSize: '16px', fontWeight: 'bold' }}>
          간편인증에 실패했습니다
        </p>
        <p style={{ color: '#666', fontSize: '14px' }}>{error.message}</p>
        <button
          onClick={() => {
            const returnPath = sessionStorage.getItem('auth_return_path');
            if (returnPath) {
              window.location.href = returnPath;
            } else if (window.opener) {
              window.close();
            } else {
              window.history.back();
            }
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: '#333',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          돌아가기
        </button>
      </div>
    );
  }

  if (result) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <p style={{ color: '#27ae60', fontSize: '16px', fontWeight: 'bold' }}>
          간편인증이 완료되었습니다
        </p>
        <p style={{ color: '#666', fontSize: '14px' }}>
          잠시 후 자동으로 이동합니다...
        </p>
      </div>
    );
  }

  return null;
}

export default EZIOKCallback;
